import { useTranslation } from "react-i18next";
import { useParallax } from "@/hooks/useParallax";
import { useEffect, useRef, useState } from "react";

export default function CTABanner() {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const handleClick = () => {
    const el = document.querySelector("#contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const { ref: bgRef, offset } = useParallax(0.2);
  const { ref: imgRef, offset: imgOffset } = useParallax(0.12);

  return (
    <section
      ref={sectionRef}
      className={`relative py-14 md:py-24 bg-[#080F1A] overflow-hidden transition-all duration-1000 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      {/* Human background image with subtle parallax */}
      <div ref={imgRef} className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Team%20of%20diverse%20business%20professionals%20collaborating%20and%20smiling%20in%20a%20modern%20bright%20office%2C%20warm%20natural%20light%2C%20approachable%20and%20trustworthy%20atmosphere%2C%20professional%20editorial%20photography%2C%20human%20centered%20composition%2C%20soft%20depth%20of%20field%2C%20cinematic%20corporate%20feel&width=1920&height=1080&seq=cta-bg-human-team-01&orientation=landscape"
          srcSet="https://readdy.ai/api/search-image?query=Team%20of%20diverse%20business%20professionals%20collaborating%20and%20smiling%20in%20a%20modern%20bright%20office%2C%20warm%20natural%20light%2C%20approachable%20and%20trustworthy%20atmosphere%2C%20professional%20editorial%20photography%2C%20human%20centered%20composition%2C%20soft%20depth%20of%20field%2C%20cinematic%20corporate%20feel%201920wquery=Team%20of%20diverse%20business%20professionals%20collaborating%20and%20smiling%20in%20a%20modern%20bright%20office%2C%20warm%20natural%20light%2C%20approachable%20and%20trustworthy%20atmosphere%2C%20professional%20editorial%20photography%2C%20human%20centered%20composition%2C%20soft%20depth%20of%20field%2C%20cinematic%20corporate%20feel%201280w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1920query=Team%20of%20diverse%20business%20professionals%20collaborating%20and%20smiling%20in%20a%20modern%20bright%20office%2C%20warm%20natural%20light%2C%20approachable%20and%20trustworthy%20atmosphere%2C%20professional%20editorial%20photography%2C%20human%20centered%20composition%2C%20soft%20depth%20of%20field%2C%20cinematic%20corporate%20feel%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&width=640&height=360&seq=cta-bg-human-team-01&orientation=landscape&height=720&seq=cta-bg-human-team-01&orientation=landscape&height=1080&seq=cta-bg-human-team-01&orientation=landscape"
          sizes="100vw"
          alt={t("cta.title")}
          className="absolute -top-[10%] left-0 w-full h-[120%] object-cover object-center"
          style={{ transform: `translateY(${imgOffset}px)` }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[#080F1A]/78" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080F1A] via-[#080F1A]/70 to-[#080F1A]/60" />
      </div>

      {/* Animated gradient background */}
      <div className="absolute inset-0 opacity-30">
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at 30% 20%, rgba(74,125,191,0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(107,155,212,0.1) 0%, transparent 50%)",
          }}
        />
      </div>

      {/* Parallax floating orbs */}
      <div ref={bgRef} className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-[15%] left-[10%] w-72 h-72 rounded-full bg-[#4A7DBF]/[0.06] blur-3xl"
          style={{ transform: `translateY(${offset * 0.8}px)` }}
        />
        <div
          className="absolute bottom-[10%] right-[15%] w-80 h-80 rounded-full bg-[#6B9BD4]/[0.05] blur-3xl"
          style={{ transform: `translateY(${offset * -0.6}px)` }}
        />
        <div
          className="absolute top-[40%] right-[30%] w-40 h-40 rounded-full bg-[#8AB8E8]/[0.04] blur-2xl"
          style={{ transform: `translateY(${offset * 0.4}px)` }}
        />
      </div>

      {/* Tech grid overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(74,125,191,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(74,125,191,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Top line */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#4A7DBF]/30 to-transparent" />
      {/* Bottom line */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#4A7DBF]/20 to-transparent" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14">
            {/* Text side */}
            <div className="lg:w-[58%] text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#4A7DBF]/20 bg-[#4A7DBF]/[0.06] mb-5">
                <span className="w-2 h-2 rounded-full bg-[#4A7DBF] animate-pulse" />
                <span className="text-[10px] font-label font-semibold text-[#6B9BD4] uppercase tracking-[0.2em]">
                  {t("cta.tag")}
                </span>
              </div>

              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4 leading-tight">
                {t("cta.title")}{" "}
                <span className="bg-gradient-to-r from-[#6B9BD4] via-[#8AB8E8] to-[#B8D4F0] bg-clip-text text-transparent">
                  {t("cta.highlight")}
                </span>
              </h2>
              <p className="text-sm md:text-base text-white/60 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                {t("cta.subtitle")}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <button
                  type="button"
                  onClick={handleClick}
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#4A7DBF] to-[#6B9BD4] text-white font-label font-semibold text-sm hover:from-[#3A6DAF] hover:to-[#5A8BC4] hover:scale-105 transition-all duration-500 cursor-pointer whitespace-nowrap shadow-[0_0_30px_rgba(74,125,191,0.25)] hover:shadow-[0_0_40px_rgba(74,125,191,0.35)]"
                >
                  {t("cta.button")}
                  <i className="ri-arrow-right-line" />
                </button>

                <a
                  href="mailto:hola@openlabsecure.com"
                  className="inline-flex items-center gap-2 text-sm text-white/40 hover:text-[#6B9BD4] transition-colors duration-300"
                >
                  <i className="ri-mail-line" />
                  hola@openlabsecure.com
                </a>
              </div>
            </div>

            {/* Human image side with tech frame */}
            <div className="lg:w-[42%] flex justify-center">
              <div className="relative">
                {/* Glow behind image */}
                <div className="absolute -inset-6 rounded-2xl bg-gradient-to-br from-[#4A7DBF]/15 via-[#6B9BD4]/5 to-transparent blur-xl" />

                {/* Frame container */}
                <div className="relative w-64 h-64 md:w-72 md:h-72 rounded-2xl overflow-hidden border border-[#4A7DBF]/20 group">
                  {/* Animated gradient border */}
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[#4A7DBF] via-[#6B9BD4] to-[#8AB8E8] opacity-30 animate-[gradientRotate_8s_linear_infinite] z-0" />

                  <div className="relative m-[1px] rounded-2xl overflow-hidden h-full">
                    <img
                      src="https://public.readdy.ai/ai/img_res/edited_c09b4914e1f706cdbe864c76fb31bf57_db045aad.jpg"
                      alt={t("cta.consultantRole")}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    {/* Overlay label */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0B1628]/90 via-[#0B1628]/40 to-transparent p-5 pt-12">
                      <p className="text-white text-sm font-heading font-semibold">{t("cta.consultantName")}</p>
                      <p className="text-white/60 text-[10px]">{t("cta.consultantRole")}</p>
                    </div>

                    {/* Corner brackets */}
                    <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 border-[#4A7DBF]/40 rounded-tl-sm" />
                    <div className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 border-[#4A7DBF]/40 rounded-tr-sm" />
                    <div className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 border-[#4A7DBF]/40 rounded-bl-sm" />
                    <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 border-[#4A7DBF]/40 rounded-br-sm" />
                  </div>
                </div>

                {/* Floating stat badge */}
                <div className="absolute -bottom-3 -left-4 bg-[#0B1628] border border-[#4A7DBF]/20 rounded-xl px-3 py-2 shadow-lg">
                  <p className="text-[9px] font-label text-[#6B9BD4] uppercase tracking-wider">{t("cta.response")}</p>
                  <p className="text-sm font-heading font-bold text-white">{t("cta.responseTime")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gradientRotate {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
      `}</style>
    </section>
  );
}